import type { CSSProperties, HTMLAttributes, ReactElement, ReactNode } from "react";
import { color, radius, space } from "@tokens/index";
import { Avatar } from "@primitives/Avatar";
import { Text } from "@primitives/Text";
import type { UserSearchResult } from "../../../experience-shell/queues";
import { formatMemberPrimaryLabel, formatMemberSecondaryLabel } from "./labels";

/**
 * One HR search hit: 36px row, initials avatar, name, muted email. Shared
 * by the queue member picker and the management-group pickers so both
 * surfaces read the same. `active` is the keyboard highlight; the caller
 * owns `role="option"` and the aria wiring through `...rest`.
 */

export interface UserResultRowProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  readonly user: UserSearchResult;
  readonly active: boolean;
  readonly trailing?: ReactNode;
}

function rowStyle(active: boolean): CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: space[2],
    minHeight: 36,
    paddingInline: space[2],
    borderRadius: radius.md,
    background: active ? color.bg.hover : "transparent",
    cursor: "pointer",
    outline: "none",
    minWidth: 0,
  };
}

export function UserResultRow({
  user,
  active,
  trailing,
  style,
  ...rest
}: UserResultRowProps): ReactElement {
  const primary = formatMemberPrimaryLabel(user);
  const secondary = formatMemberSecondaryLabel(user);
  return (
    <div {...rest} style={{ ...rowStyle(active), ...style }}>
      <Avatar alt={primary} size="sm" />
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: space[2],
          flex: 1,
          minWidth: 0,
        }}
      >
        <Text role="bodySm" weight="medium" truncate>
          {primary}
        </Text>
        {secondary !== null && secondary !== primary && (
          <Text role="caption" tone="muted" truncate>
            {secondary}
          </Text>
        )}
      </div>
      {trailing}
    </div>
  );
}
